import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldAlert, Loader2, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import DividendFilterSegmentedControl from '@/components/DividendFilterSegmentedControl';
import { useAuth } from '@/contexts/AuthContext';
import { useMarketTheme } from '@/hooks/useMarketTheme';
import StockModal from '@/components/StockModal';
import CompareModal from '@/components/CompareModal';
import LoginModal from '@/components/LoginModal';
import { fetchStockWithFallback } from '@/lib/stockFetch';
import { useReopenModalOnMount } from '@/lib/stockModalRestore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GUIDE = '/learn/accounting-anomalies-enron-checklist';
const fmtCap = (v) => (v == null ? '—' : v >= 1e12 ? `$${(v / 1e12).toFixed(1)}T` : v >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : `$${(v / 1e6).toFixed(0)}M`);
const tone = (n) => (n === 0 ? 'text-emerald-600 dark:text-emerald-400' : n <= 2 ? 'text-amber-600 dark:text-amber-400' : 'text-rose-600 dark:text-rose-400');

function StockRow({ r, i, onSelect }) {
  return (
    <tr className={`cursor-pointer hover:bg-fuchsia-500/10 ${i % 2 ? 'bg-sky-100/60 dark:bg-sky-950/25' : 'bg-emerald-100/60 dark:bg-emerald-950/25'}`} onClick={() => onSelect(r.ticker)} data-testid={`hub-row-${r.ticker}`}>
      <td className="px-2 py-1.5">
        <button type="button" onClick={(e) => { e.stopPropagation(); onSelect(r.ticker); }} className="font-mono font-bold text-primary hover:underline" data-testid={`hub-link-${r.ticker}`}>{r.ticker}</button>
        <span className="block text-[11px] text-muted-foreground truncate max-w-[180px]">{r.name}</span>
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums">{fmtCap(r.market_cap)}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">{r.yield != null && r.pays_dividend ? `${Number(r.yield).toFixed(2)}%` : '—'}</td>
      <td className={`px-2 py-1.5 text-right font-bold tabular-nums ${tone(r.n_flagged)}`}>{r.n_flagged}{r.n_computed ? ` / ${r.n_computed}` : ''}</td>
      <td className="px-2 py-1.5 text-[11px] text-muted-foreground">{r.item_names.join(', ') || 'Clean on every computed item'}</td>
    </tr>
  );
}

function StockTable({ rows, testId, onSelect }) {
  if (!rows.length) return <p className="text-xs text-muted-foreground px-2 py-2">None in this group.</p>;
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full text-xs">
        <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground"><th className="text-left px-2 py-1">Stock</th><th className="text-right px-2 py-1">Mkt cap</th><th className="text-right px-2 py-1">Yield</th><th className="text-right px-2 py-1">Items</th><th className="text-left px-2 py-1">Items flagged</th></tr></thead>
        <tbody>{rows.map((r, i) => <StockRow key={r.ticker} r={r} i={i} onSelect={onSelect} />)}</tbody>
      </table>
    </div>
  );
}

function SectorCard({ g, onSelect }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-border/60 bg-card p-3" data-testid={`hub-sector-${g.sector.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-3 text-left" data-testid={`hub-sector-toggle-${g.sector.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
        <div className="min-w-0">
          <div className="text-sm font-bold">{g.sector}</div>
          <div className="text-[11px] text-muted-foreground">{g.scored} scored · <span className="text-emerald-600 dark:text-emerald-400">{g.clean} clean</span> · <span className="text-rose-600 dark:text-rose-400">{g.flagged3} with 3+</span> · avg {g.avg_items} items{g.top_items?.length ? ` · most common: ${g.top_items.map(([n, c]) => `${n} (${c})`).join(', ')}` : ''}</div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
      </button>
      {open && (
        <div className="mt-3 grid gap-3 lg:grid-cols-2 min-w-0">
          <div className="min-w-0"><div className="text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400 px-2 mb-1">Cleanest (0 items, largest first)</div><StockTable rows={g.cleanest} testId="hub-sector-cleanest" onSelect={onSelect} /></div>
          <div className="min-w-0"><div className="text-[10px] uppercase tracking-wider text-rose-600 dark:text-rose-400 px-2 mb-1">Most flagged (3+ items)</div><StockTable rows={g.most_flagged} testId="hub-sector-most-flagged" onSelect={onSelect} /></div>
        </div>
      )}
    </div>
  );
}

export default function AccountingAnomaliesHub() {
  const { user } = useAuth();
  const isAdmin = (user?.role || '') === 'admin';
  const navigate = useNavigate();
  const { isDark } = useMarketTheme();
  const [filter, setFilter] = useState('dividend');
  const [d, setD] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareTicker, setCompareTicker] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  useEffect(() => {
    if (!user) { setWatchlist([]); return; }
    axios.get(`${API}/portfolio`, { withCredentials: true }).then((r) => setWatchlist(r.data.map((p) => p.ticker))).catch(() => {});
  }, [user]);
  const toggleWatchlist = async (ticker) => {
    if (!user) { setLoginOpen(true); return; }
    const inList = watchlist.includes(ticker);
    try {
      if (inList) await axios.delete(`${API}/portfolio/${ticker}`, { withCredentials: true });
      else await axios.post(`${API}/portfolio`, { ticker }, { withCredentials: true });
      setWatchlist((w) => (inList ? w.filter((t) => t !== ticker) : [...w, ticker]));
    } catch { /* ignore */ }
  };
  const handleStockClick = async (ticker) => {
    setLoadingDetail(true);
    setModalOpen(true);
    try {
      const s = await fetchStockWithFallback(ticker, { timeoutMs: 3500, onFresh: (fresh) => setSelectedStock(fresh) });
      setSelectedStock(s);
    } catch { setSelectedStock(null); }
    finally { setLoadingDetail(false); }
  };
  useReopenModalOnMount(handleStockClick);
  useEffect(() => {
    // SEO head — mirrors MegaTier. Canonical is param-free so the dividend/all variants collapse.
    document.title = 'Accounting Anomalies by Sector — Cleanest and Most-Flagged Dividend Stocks | TwylightCrow';
    const canonical = 'https://twylightcrow.com/accounting-anomalies';
    const ogTitle = 'Accounting Anomalies by Sector — the Enron Checklist Scored for Every Stock';
    const ogDesc = 'Ten forensic checks (Beneish M-Score, Montier C-Score, DSRI, ROIC vs WACC, leverage, goodwill, Exhibit 21 complexity, auditor fees and tenure, related parties, 8-K departures) scored weekly. Cleanest and most-flagged dividend payers ranked by sector with the item breakdown.';
    const ogImage = 'https://twylightcrow.com/dividrip-icon.png?v=3';
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('name', 'description', ogDesc);
    setMeta('name', 'keywords', 'accounting red flags, Beneish M-Score screen, earnings manipulation, forensic accounting stocks, Montier C-Score, Enron checklist, dividend stock accounting quality, auditor fees, related party transactions');
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'TwylightCrow — DiviDrip');
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:title', ogTitle);
    setMeta('property', 'og:description', ogDesc);
    setMeta('property', 'og:image', ogImage);
    setMeta('name', 'twitter:card', 'summary');
    setMeta('name', 'twitter:title', ogTitle);
    setMeta('name', 'twitter:description', ogDesc);
    setMeta('name', 'twitter:image', ogImage);
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement('link'); link.setAttribute('rel', 'canonical'); document.head.appendChild(link); }
    link.setAttribute('href', canonical);
  }, []);
  useEffect(() => {
    let alive = true; setD(null);
    axios.get(`${API}/accounting-anomalies/hub`, { params: { filter }, withCredentials: true }).then((r) => alive && setD(r.data)).catch(() => alive && setD({ error: true }));
    return () => { alive = false; };
  }, [filter]);
  const maxFreq = d?.item_freq ? Math.max(1, ...d.item_freq.map((x) => x.count)) : 1;
  return (
    <div className="min-h-screen bg-background" data-testid="anomalies-hub-page">
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-0">
        <BackOrHomeButton testid="anomalies-hub-back-or-home-btn" showEscHint={false} />
        <div className="mt-4 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2"><ShieldAlert className="w-6 h-6 text-fuchsia-500" /> Accounting Anomalies by Sector</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl" data-testid="anomalies-hub-subtitle">
              The ten-item Enron checklist (Beneish M, C-Score, DSRI, ROIC vs WACC, leverage, goodwill, Exhibit 21 complexity, auditor fees and tenure, related parties, 8-K departures) scored weekly for every company with filed statements. Anomalies are <strong>questions, not verdicts</strong> — utilities and REITs trip leverage by design; the point is to know which questions to ask before buying.{' '}
              <Link to={GUIDE} className="text-primary hover:underline inline-flex items-center gap-1" data-testid="anomalies-hub-guide-link"><BookOpen className="w-3.5 h-3.5" /> Read the guide</Link>
            </p>
          </div>
          <DividendFilterSegmentedControl value={filter} isAdmin={isAdmin} instanceId="anomalies-hub" onChange={setFilter} />
        </div>
        {!d && <div className="py-10 flex items-center gap-2 text-sm text-muted-foreground" data-testid="anomalies-hub-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading the weekly scores…</div>}
        {d?.error && <p className="py-10 text-sm text-rose-500" data-testid="anomalies-hub-error">Could not load the hub. Try again shortly.</p>}
        {d && !d.error && (
          <>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="anomalies-hub-stats">
              <div className="rounded-md border border-border/60 p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Scored</div><div className="font-mono text-lg font-bold">{d.scored.toLocaleString()}</div></div>
              <div className="rounded-md border border-border/60 p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Clean (0 items)</div><div className="font-mono text-lg font-bold text-emerald-600 dark:text-emerald-400">{d.clean.toLocaleString()}</div></div>
              <div className="rounded-md border border-border/60 p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">3+ items</div><div className="font-mono text-lg font-bold text-rose-600 dark:text-rose-400">{d.flagged3.toLocaleString()}</div></div>
              <div className="rounded-md border border-border/60 p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Sectors</div><div className="font-mono text-lg font-bold">{d.sectors.length}</div></div>
            </div>
            <div className="mt-4 rounded-lg border border-border/60 bg-card p-3" data-testid="anomalies-hub-item-freq">
              <div className="text-xs font-bold uppercase tracking-wider text-fuchsia-600 dark:text-fuchsia-300 mb-2">Which items trip most often</div>
              <div className="space-y-1">
                {d.item_freq.map((x) => (
                  <div key={x.item} className="flex items-center gap-2 text-xs"><span className="w-40 shrink-0 truncate">{x.item}. {x.name}</span><div className="flex-1 h-2 rounded bg-muted/50 overflow-hidden"><div className="h-full bg-fuchsia-500/70" style={{ width: `${(x.count / maxFreq) * 100}%` }} /></div><span className="w-14 text-right font-mono tabular-nums">{x.count}</span></div>
                ))}
              </div>
            </div>
            <h2 className="mt-6 text-base md:text-lg font-bold">Sectors, cleanest first</h2>
            <div className="mt-2 space-y-2" data-testid="anomalies-hub-sectors">{d.sectors.map((g) => <SectorCard key={g.sector} g={g} onSelect={handleStockClick} />)}</div>
            <h2 className="mt-6 text-base md:text-lg font-bold text-emerald-600 dark:text-emerald-400">Cleanest overall — 0 items, largest companies</h2>
            <div className="mt-2 rounded-lg border border-border/60 bg-card p-2"><StockTable rows={d.cleanest_overall} testId="anomalies-hub-cleanest" onSelect={handleStockClick} /></div>
            <h2 className="mt-6 text-base md:text-lg font-bold text-rose-600 dark:text-rose-400">Most flagged overall — 4+ items</h2>
            <div className="mt-2 rounded-lg border border-border/60 bg-card p-2"><StockTable rows={d.most_flagged_overall} testId="anomalies-hub-most-flagged" onSelect={handleStockClick} /></div>
            <p className="mt-3 text-[11px] text-muted-foreground">Scores refresh weekly (Sunday chain). Companies without three annual statements are not scored. As of {new Date(d.as_of).toLocaleDateString()}.</p>
          </>
        )}
        <SiteDisclaimer />
      </div>
      <StockModal
        stock={selectedStock}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedStock(null); }}
        loading={loadingDetail}
        isInWatchlist={selectedStock ? watchlist.includes(selectedStock.ticker) : false}
        onToggleWatchlist={toggleWatchlist}
        isDark={isDark}
        user={user}
        onLoginRequired={() => setLoginOpen(true)}
        onPositionsChanged={() => window.dispatchEvent(new Event('portfolio-changed'))}
        onViewPortfolio={() => { setModalOpen(false); navigate('/?view=portfolio'); }}
        onCompareClick={(t) => { if (t) { setCompareTicker(t); setCompareOpen(true); } }}
        initialTab="financial-health"
        initialChartPeriod={null}
        onSelectStock={handleStockClick}
        watchlist={watchlist}
      />
      <CompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        initialTickerA={compareTicker}
        watchlist={watchlist}
        onTickerClick={() => {}}
      />
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}
